import React, { useState } from 'react';
import { LucideIcon, ChevronDown } from 'lucide-react';

export type FlowType = 'convert' | 'revive' | 'engage';

interface FlowDescriptionCardProps {
  /** The type of flow - determines the color scheme */
  flowType: FlowType;
  /** Main description text - can be a string or array of strings for multiple lines */
  description: string | string[];
  /** Icon component from lucide-react */
  icon: LucideIcon;
  /** Optional tags to display (e.g., lead sources) */
  tags?: string[];
  /** Optional label for tags section */
  tagsLabel?: string;
  /** Optional data-tour attribute for guided tours */
  dataTour?: string;
}

export const FlowDescriptionCard: React.FC<FlowDescriptionCardProps> = ({
  flowType,
  description,
  icon: Icon,
  tags,
  tagsLabel = 'Lead flow examples:',
  dataTour,
}) => {
  const descriptionLines = Array.isArray(description) ? description : [description];
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="flex items-start gap-3 text-xs sm:text-sm sm:leading-relaxed bg-amber-50 dark:bg-amber-500/10 p-3 sm:p-4 rounded-lg border border-amber-200 dark:border-amber-500/20"
      data-tour={dataTour}
    >
      <div className="hidden sm:flex p-1.5 bg-amber-100 dark:bg-amber-500/15 rounded-lg flex-shrink-0">
        <Icon className="w-4 h-4 text-amber-700 dark:text-amber-300" />
      </div>
      <div className="flex-1 min-w-0 text-amber-900 dark:text-amber-200 space-y-1.5">
        {/* Mobile: collapsed by default, shows first line only with chevron toggle */}
        <div className="sm:hidden">
          {expanded ? (
            descriptionLines.map((line, i) => <p key={i} className="mb-1.5 last:mb-0">{line}</p>)
          ) : (
            <p className="line-clamp-2">{descriptionLines[0]}</p>
          )}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-amber-900 dark:text-amber-200 hover:underline"
          >
            {expanded ? 'Show less' : 'Show more'}
            <ChevronDown
              className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {/* Desktop: always fully expanded */}
        <div className="hidden sm:block space-y-1.5">
          {descriptionLines.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>

        {/* Tags — only on desktop or when mobile is expanded */}
        {tags && tags.length > 0 && (
          <div
            className={`flex-wrap items-center gap-1.5 pt-1 sm:flex ${
              expanded ? 'flex' : 'hidden'
            }`}
          >
            <span className="text-xs font-medium">{tagsLabel}</span>
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300 text-xs font-medium border border-amber-200 dark:border-amber-500/30"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
